/*!

=========================================================
* Argon Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, Switch, Redirect } from "react-router-dom";

import AdminNavbar from "components/Navbars/AdminNavbar.js";
import Sidebar from "components/Sidebar/Sidebar.js";
import { useHistory } from "react-router-dom";
import routes from "routes.js";
import useAuth from "hooks/useAuth";
import PrivateRoute from "components/PrivateRoute";
import { userService } from "services";
import NotifyComponent, { showNotify } from "components/notifications/notify";
import { describeApiError } from "services/apiErrorMessage";

const SESSION_ENDING_STATUS = [401, 403];

function Admin({ ...props }) {
  const [sidenavOpen, setSidenavOpen] = useState(true);
  const location = useLocation();
  const mainContentRef = useRef(null);
  const notifyRef = useRef(null);
  const history = useHistory();
  const { isAuthenticated, loading, authorId, signOut } = useAuth();

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!isAuthenticated) {
      history.replace("/auth/login");
    }
  }, [history, isAuthenticated, loading]);

  useEffect(() => {
    if (!isAuthenticated || !authorId) {
      return;
    }

    const date = new Date().toISOString();
    userService
      .updateLastLogin(authorId, date)
      .catch((error) => {
        if (error.response) {
          const detail = error.response.data.detail;
          if (detail === "Token inválido.") {
            signOut();
            history.replace("/auth/login");
            return;
          }
          return console.info(detail);
        }
        return console.error(error);
      });
  }, [authorId, history, isAuthenticated, signOut]);

  const getPagePermissions = useCallback(() => {
    if (!authorId) {
      return;
    }

    userService
      .getById(authorId)
      .then((response) => {
        // if ((responses[0].data.length === 0) && !(responses[1].data.is_superuser)) {
        //   localStorage.clear();
        //   history.push("/auth/login");
        // }

        if (response.data.is_superuser || true) {
          // const pageNames = responses[2].data.map((page) => page.name);
          // pagesAllowed = pagesAllowed.concat(pageNames);
          // return setPageState(pagesAllowed);
        } else {
          // api.get(`/permissions/${responses[0].data[0].idPermission}`).then((permission) => {
          //   responses[2].data.forEach((page) => {
          //     if (permission.data.idPages.indexOf(page.idPage) > -1){
          //       pagesAllowed.push(page.name);
          //     }
          //   });
          //   setPageState(pagesAllowed);
          // }).catch((error) => {
          //   console.error(error);
          //   window.alert("An error occurred while trying to collect your permissions. [1]");
          //   localStorage.clear();
          //   history.push("/auth/login");
          // });
        }
      })
      .catch((error) => {
        console.error(error);
        // Só 401/403 encerram a sessão. Antes qualquer falha (rede, timeout,
        // restart da API) deslogava com um window.alert que travava a aba
        // (precatorios_web#13).
        if (SESSION_ENDING_STATUS.includes(error.response?.status)) {
          signOut();
          history.push("/auth/login", { authError: describeApiError(error) });
          return;
        }
        showNotify(
          notifyRef,
          "Aviso",
          `Não foi possível carregar suas permissões. ${describeApiError(error)}`,
          "warning"
        );
      });
  }, [authorId, history, signOut]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    mainContentRef.current.scrollTop = 0;
  }, [location]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    getPagePermissions();
  }, [getPagePermissions, isAuthenticated]);

  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      if (prop.collapse) {
        return getRoutes(prop.views);
      }

      if (prop.layout === "/admin") {
        // if(pageState.indexOf(prop.name.toLowerCase()) === -1) return (
        //   <Route
        //     path={prop.layout + prop.path}
        //     render={() => <Unauthorized/>}
        //     key={key}
        //   />
        // );

        return (
          <PrivateRoute
            path={prop.layout + prop.path}
            component={prop.component}
            key={key}
          />
        );
      } else {
        return null;
      }
    });
  };
  const getBrandText = (path) => {
    for (let i = 0; i < routes.length; i++) {
      if (location.pathname.indexOf(routes[i].layout + routes[i].path) !== -1) {
        return routes[i].name;
      }
    }
    return "Brand";
  };
  // toggles collapse between mini sidenav and normal
  const toggleSidenav = (e) => {
    if (document.body.classList.contains("g-sidenav-pinned")) {
      document.body.classList.remove("g-sidenav-pinned");
      document.body.classList.add("g-sidenav-hidden");
    } else {
      document.body.classList.add("g-sidenav-pinned");
      document.body.classList.remove("g-sidenav-hidden");
    }
    setSidenavOpen(!sidenavOpen);
  };
  const getNavbarTheme = () => {
    return location.pathname.indexOf("admin/alternative-dashboard") === -1
      ? "dark"
      : "light";
  };

  return (
    <>
      <NotifyComponent notifyRef={notifyRef} />
      {loading ? (
        <div className="main-content" ref={mainContentRef}>
          <div className="py-5 text-center">Carregando...</div>
        </div>
      ) : (
        <>
          {!isAuthenticated ? (
            <Redirect to="/auth/login" />
          ) : (
            <>
              <Sidebar
                routes={routes}
                toggleSidenav={toggleSidenav}
                sidenavOpen={sidenavOpen}
                logo={{
                  innerLink: "/admin/home",
                  imgSrc: require("assets/img/custom/company/logo2.png"),
                  imgAlt: "...",
                }}
              />
              <div className="main-content" ref={mainContentRef}>
                <AdminNavbar
                  theme={getNavbarTheme()}
                  toggleSidenav={toggleSidenav}
                  sidenavOpen={sidenavOpen}
                  brandText={getBrandText(location.pathname)}
                />
                <Switch>
                  {getRoutes(routes)}
                  <Redirect from="*" to="/admin/home" />
                </Switch>
              </div>
              {sidenavOpen ? (
                <div className="backdrop d-xl-none" onClick={toggleSidenav} />
              ) : null}
            </>
          )}
        </>
      )}
    </>
  );
}

export default Admin;
